import {
    useMemo,
    useRef,
    useState,
} from 'react'
import Typography from '@mui/material/Typography'

import Card from 'app/shared-components/Card'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    ContainerAvailabilityAllowedProductTypes,
} from 'app/enums/label.enums'
import {
    ContainerAvailabilityForecastContainerStatuses,
} from 'app/enums/common.enums'
import {
    ForecastAvailabilityOptions,
} from '../../serviceCenters.types'
import useGetAvailability from '../../hooks/useGetAvailability'

import useStyles from './ContainerAvailabilityForecast.styles'
import ContainerAvailabilityForecastChart from './ContainerAvailabilityForecastChart'
import OptionPanel from './OptionPanel'
import AggregatedChart from './AggregatedChart'

const ContainerAvailabilityForecast = ({
    id,
    defaultProductType,
    productTypes,
}: {
    id: number,
    defaultProductType: ContainerAvailabilityAllowedProductTypes,
    productTypes: Record<string, string>,
}) => {
    const {
        classes,
    } = useStyles()
    const allowedValues = {
        ALL: 'ALL',
        ...productTypes,
    }
    const [
        value,
        setValue,
    ] = useState<ForecastAvailabilityOptions>({
        containerStatus: {
            [ContainerAvailabilityForecastContainerStatuses.ToBeProcessed]: true,
            [ContainerAvailabilityForecastContainerStatuses.InProcess]: true,
            [ContainerAvailabilityForecastContainerStatuses.Processed]: true,
            [ContainerAvailabilityForecastContainerStatuses.InPreconditioning]: true,
            [ContainerAvailabilityForecastContainerStatuses.Preconditioned]: true,
        },
        productType: defaultProductType,
    })

    const [
        includesOrders,
        setIncludesOrders,
    ] = useState<{
        includeNotStartedOrders?: boolean,
        includePreBookings?: boolean,
        includeAllDualContainers?: boolean,
    }>({
        includeNotStartedOrders: false,
        includePreBookings: false,
        includeAllDualContainers: true,
    })

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetAvailability({
        serviceCenter: {
            id,
        },
        ...includesOrders,
    })

    const listStatuses = useRef(Object.keys(value.containerStatus))

    const dataChart = useMemo(() => {
        return data?.forecastsByProduct?.find(({
            productType,
        }) => {
            return productType === value.productType
        })
    }, [
        data?.forecastsByProduct,
        value.productType,
    ])

    return (
        <Card
            contentClass={classes.cardContent}
            data-testid="availability-forecast-card"
            title="Container Availability Forecast"
        >
            <OptionPanel
                value={value}
                onChange={setValue}
                allowedValues={allowedValues}
                includesOrders={includesOrders}
                setIncludesOrders={setIncludesOrders}
            />
            <StatusHandler
                isSuccess={isDataReady}
                isFetching={isFetching}
                isError={isError}
                error={error}
            >
                <>
                    <ContainerAvailabilityForecastChart
                        value={dataChart?.forecast}
                        listStatuses={listStatuses.current}
                        containerStatus={value.containerStatus}
                        minPreconditioned={dataChart?.minPreconditioned}
                    />
                    <Typography
                        variant="h3"
                        className={classes.title}
                    >
                        Total Usable Containers
                    </Typography>
                    <AggregatedChart
                        value={dataChart?.forecast}
                        containerStatus={value.containerStatus}
                        safeMinimumUsableStock={dataChart?.minUsable}
                    />
                </>
            </StatusHandler>
        </Card>
    )
}

export default ContainerAvailabilityForecast
